import { onBeforeUnmount, ref, watch } from "vue";

export type ManualSharingChoice = "veeva" | "outlook";
export type ManualSharingPhase =
  | "choose"
  | "saving"
  | "preparing"
  | "copying"
  | "save_error"
  | "prepare_error"
  | "copy_error";
type CopyRequest = {
  persist: (
    attemptId: string,
    choice: ManualSharingChoice,
    signal: AbortSignal,
  ) => Promise<void>;
  // Prepare links without performing the external clipboard action. Keeping the
  // prepared action lets a browser-denied copy be retried in a fresh click.
  prepare: (signal: AbortSignal) => Promise<() => Promise<void>>;
  onSuccess: () => void;
  onError: (error: unknown, phase: ManualSharingPhase) => boolean;
};
type CopyAttempt = CopyRequest & {
  controller: AbortController;
  required: boolean;
  running: boolean;
  attemptId?: string;
  choice?: ManualSharingChoice;
  persisted: boolean;
  copy?: () => Promise<void>;
};

export const useManualSharingConsent = ({
  required,
  eligible,
  context,
}: {
  required: () => boolean;
  eligible: () => boolean;
  context: () => string;
}) => {
  const active = ref(false);
  const busy = ref(false);
  const phase = ref<ManualSharingPhase>("choose");
  let attempt: CopyAttempt | undefined;
  let unmounted = false;
  const cancel = () => {
    attempt?.controller.abort();
    attempt = undefined;
    active.value = false;
    busy.value = false;
  };
  const valid = (current: CopyAttempt) =>
    attempt === current &&
    !current.controller.signal.aborted &&
    !unmounted &&
    eligible();

  const execute = async (current: CopyAttempt) => {
    if (!valid(current) || current.running) return;
    current.running = true;
    try {
      if (current.required && !current.persisted) {
        phase.value = "saving";
        await current.persist(
          current.attemptId!,
          current.choice!,
          current.controller.signal,
        );
        current.persisted = true;
      }
      if (!valid(current)) return;
      if (!current.copy) {
        phase.value = "preparing";
        current.copy = await current.prepare(current.controller.signal);
      }
      if (!valid(current)) return;
      phase.value = "copying";
      // With a prepared action, retry reaches this call synchronously in the
      // user's click. Never repeat persistence or voucher creation for it.
      await current.copy();
      if (valid(current)) {
        current.onSuccess();
        cancel();
      }
    } catch (error) {
      if (!valid(current)) return;
      if (
        !current.required ||
        (phase.value === "preparing" && current.onError(error, phase.value))
      ) {
        if (!current.required) current.onError(error, phase.value);
        cancel();
        return;
      }
      phase.value =
        phase.value === "saving"
          ? "save_error"
          : phase.value === "preparing"
            ? "prepare_error"
            : "copy_error";
    } finally {
      current.running = false;
    }
  };
  const request = (operation: CopyRequest) => {
    if (unmounted || busy.value || !eligible()) return;
    const current: CopyAttempt = {
      ...operation,
      controller: new AbortController(),
      required: required(),
      persisted: false,
      running: false,
    };
    attempt = current;
    busy.value = true;
    phase.value = "choose";
    if (current.required) {
      active.value = true;
      return;
    }
    return execute(current);
  };
  const confirm = (choice: ManualSharingChoice) => {
    if (
      !active.value ||
      !attempt ||
      phase.value !== "choose" ||
      !["veeva", "outlook"].includes(choice)
    )
      return;
    if (!eligible()) {
      cancel();
      return;
    }
    attempt.choice = choice;
    attempt.attemptId = crypto.randomUUID();
    return execute(attempt);
  };
  const retry = () => {
    if (attempt && active.value && phase.value.endsWith("_error"))
      return execute(attempt);
  };
  watch([context, eligible, required], cancel, { flush: "sync" });
  onBeforeUnmount(() => {
    unmounted = true;
    cancel();
  });
  return { active, busy, phase, request, confirm, retry, cancel };
};
